import type {
  DashboardMetrics,
  DashboardStatusFilter,
  ExperimentData,
  ExperimentStatus,
  SortOption,
} from "../types/dashboard.types";

const STATUS_SORT_ORDER: Record<ExperimentStatus, number> = {
  active: 0,
  paused: 1,
  completed: 2,
  locked: 3,
  draft: 4,
  deleted: 5,
};

const numberFormatter = new Intl.NumberFormat("en-US");

export const formatCount = (value: number | null | undefined) =>
  typeof value === "number" ? numberFormatter.format(value) : "-";

export const formatPercentage = (value: number | null | undefined) =>
  typeof value === "number" ? `${value}%` : "-";

export const computeLiveMetrics = (
  activeExperiments: ExperimentData[],
): DashboardMetrics => {
  const totalParticipants = activeExperiments.reduce(
    (sum, experiment) => sum + (experiment.metrics.participants ?? 0),
    0,
  );
  const completionValues = activeExperiments
    .map((experiment) => experiment.metrics.completionRate)
    .filter((value): value is number => typeof value === "number");
  const avgCompletionRate = completionValues.length
    ? Math.round(
        completionValues.reduce(
          (sum, completionRate) => sum + completionRate,
          0,
        ) / completionValues.length,
      )
    : 0;
  const totalEvents24h = activeExperiments.reduce(
    (sum, experiment) => sum + (experiment.metrics.events24h ?? 0),
    0,
  );

  return {
    activeCount: activeExperiments.length,
    totalParticipants,
    avgCompletionRate,
    totalEvents24h,
  };
};

export const computeStatusCounts = (
  experiments: ExperimentData[],
): Record<ExperimentStatus, number> => {
  return experiments.reduce(
    (acc, item) => {
      acc[item.status] += 1;
      return acc;
    },
    {
      draft: 0,
      locked: 0,
      active: 0,
      paused: 0,
      completed: 0,
      deleted: 0,
    } as Record<ExperimentStatus, number>,
  );
};

export const filterAndSortExperiments = (
  experiments: ExperimentData[],
  query: string,
  statusFilter: DashboardStatusFilter,
  sortBy: SortOption,
): ExperimentData[] => {
  const normalizedQuery = query.trim().toLowerCase();

  const result = experiments.filter((experiment) => {
    const statusMatch =
      statusFilter === "all" || experiment.status === statusFilter;
    const textMatch =
      !normalizedQuery ||
      experiment.name.toLowerCase().includes(normalizedQuery) ||
      experiment.code.toLowerCase().includes(normalizedQuery) ||
      experiment.product.toLowerCase().includes(normalizedQuery) ||
      experiment.owner.name.toLowerCase().includes(normalizedQuery) ||
      experiment.owner.shortName.toLowerCase().includes(normalizedQuery) ||
      experiment.status.toLowerCase().includes(normalizedQuery);

    return statusMatch && textMatch;
  });

  result.sort((a, b) => {
    switch (sortBy) {
      case "name":
        return a.name.localeCompare(b.name);
      case "responses":
        return (b.metrics.responses ?? -1) - (a.metrics.responses ?? -1);
      case "completion":
        return (
          (b.metrics.completionRate ?? -1) - (a.metrics.completionRate ?? -1)
        );
      case "status":
        return STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];
      case "updated":
      default:
        return a.updatedMinutesAgo - b.updatedMinutesAgo;
    }
  });

  return result;
};
